export const calculateTotals = (transactionList) => {
  const toNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  // Sales total (sum of each sale total)
  const grandTotalTransactionAmount = transactionList.reduce((sum, tx) => {
    const salesTotal = (tx.sales || []).reduce((tSum, sale) => tSum + toNumber(sale?.total), 0);
    return sum + salesTotal;
  }, 0);

  // Discounts
  const discountAmount = transactionList.reduce((sum, tx) => sum + toNumber(tx?.discount), 0);

  // Labour charges (if present)
  const labourCharges = transactionList.reduce((sum, tx) => sum + toNumber(tx?.labourCharges?.total || 0), 0);

  // Damage refunds (sum of each refund total)
  const damageRefund = transactionList.reduce((sum, tx) => {
    const dTotal = (tx.damageRefunds || []).reduce((tSum, d) => tSum + toNumber(d?.total), 0);
    return sum + dTotal;
  }, 0);

  // Payments received directly (paymentAmount)
  const grandTotalPaidAmount = transactionList.reduce((sum, tx) => sum + toNumber(tx?.paymentAmount), 0);

  // Tray payments (numberOfTrays * costPerTray) – exclude trays marked excludePayment
  const grandTrayPaymentAmount = transactionList.reduce((sum, tx) =>
    sum + (tx.trays || []).reduce((tSum, tray) => {
      if (tray?.excludePayment) return tSum;
      return tSum + (toNumber(tray?.numberOfTrays) * toNumber(tray?.costPerTray));
    }, 0)
  , 0);

  // Total paid (payment + tray payments)
  const totalPaid = grandTotalPaidAmount + grandTrayPaymentAmount;

  // Overall amount due (sales + labour) - (discount + damageRefund + totalPaid)
  const totalDueAmount = (grandTotalTransactionAmount + labourCharges) - (discountAmount + damageRefund + totalPaid);

  return {
    grandTotalTransactionAmount,
    discountAmount,
    labourCharges,
    damageRefund,
    grandTotalPaidAmount,
    grandTrayPaymentAmount,
    totalPaid,
    totalDueAmount,
  };
};

export const calculatePastTransactionsBalance = (currentTx, transactions = [], currentTrayTypes = []) => {
  if (!currentTx) return 0;
  const toNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const sourceList = Array.isArray(transactions) && transactions.length > 0
    ? transactions
    : [currentTx];

  const getTxDateTime = (t) => {
    if (t?.dateOfTransaction) {
      const d = new Date(t.dateOfTransaction);
      if (!isNaN(d.getTime())) return d.getTime();
    }
    return 0;
  };

  const getTxCreatedOrder = (t) => {
    if (t?.createdAt) {
      const d = new Date(t.createdAt);
      if (!isNaN(d.getTime())) return d.getTime();
    }
    if (t?._id) {
      const str = String(t._id);
      if (str.length === 24) {
        const timestamp = parseInt(str.substring(0, 8), 16) * 1000;
        if (!isNaN(timestamp)) return timestamp;
      }
    }
    return 0;
  };

  const compareTx = (a, b) => {
    const dateDiff = getTxDateTime(a) - getTxDateTime(b);
    if (dateDiff !== 0) return dateDiff;

    const createDiff = getTxCreatedOrder(a) - getTxCreatedOrder(b);
    if (createDiff !== 0) return createDiff;

    return String(a?._id || "").localeCompare(String(b?._id || ""));
  };

  let listToSort = [...sourceList];
  const exists = listToSort.some(
    (t) => (t?._id && currentTx?._id && String(t._id) === String(currentTx._id)) || t === currentTx
  );
  if (!exists) {
    listToSort.push(currentTx);
  }

  const sorted = listToSort.sort(compareTx);

  const currentIndex = sorted.findIndex(
    (t) => (t?._id && currentTx?._id && String(t._id) === String(currentTx._id)) || t === currentTx
  );

  const relevantTransactions = currentIndex !== -1
    ? sorted.slice(0, currentIndex + 1)
    : sorted;

  return relevantTransactions.reduce((acc, t) => {
    const sales = t.sales || [];
    const salesTotal = sales.reduce((sum, s) => {
      const lineTotal =
        s?.total != null && !isNaN(Number(s.total))
          ? Number(s.total)
          : toNumber(s?.numberOfItems) * toNumber(s?.costPerItem);
      return sum + (Number.isFinite(lineTotal) ? lineTotal : 0);
    }, 0);

    const labourTotal = t?.labourCharges?.enabled
      ? toNumber(t.labourCharges.total || 0)
      : toNumber(t?.labourCharges?.total || 0);

    const damageRefunds =
      Array.isArray(t.damageRefunds) && t.damageRefunds.length > 0
        ? t.damageRefunds
        : t.damageRefund?.enabled
        ? [t.damageRefund]
        : t.damageRefund?.total
        ? [t.damageRefund]
        : [];
    const damageRefundTotal = damageRefunds.reduce(
      (sum, d) => sum + toNumber(d?.total),
      0
    );

    const payment = toNumber(t?.paymentAmount);

    const trays = t.trays || [];
    const trayPaymentTotal = trays.reduce((sum, tray) => {
      if (tray?.excludePayment) return sum;
      const trayType =
        currentTrayTypes.find(
          (tt) => tt.trayTypeId === tray.trayTypeId || tt.id === tray.trayTypeId
        ) || null;
      const cost = toNumber(tray?.costPerTray) || trayType?.price || 0;
      return sum + toNumber(tray?.numberOfTrays) * cost;
    }, 0);

    const discount = toNumber(t?.discount);

    const debits = salesTotal + labourTotal;
    const credits = payment + trayPaymentTotal + discount + damageRefundTotal;

    return acc + (debits - credits);
  }, 0);
};

export default calculateTotals;
